/**
 * Feature Flags API Client
 */

import apiClient from './client';
import type {
  FeatureFlagsConfig,
  FeatureFlag,
  UserOverridesResponse,
  UserOverrideForUserResponse,
  UserOverrideRequest,
  AuditLogsResponse,
  GroupMembers,
} from '@/types/featureFlags';

export const featureFlagsApi = {
  /**
   * Get full feature flags configuration
   */
  getConfig: async (): Promise<FeatureFlagsConfig> => {
    const response = await apiClient.get('/system/feature-flags');
    return response.data;
  },

  /**
   * Update full feature flags configuration
   */
  updateConfig: async (
    flags: Record<string, FeatureFlag>,
    changeReason?: string
  ): Promise<FeatureFlagsConfig> => {
    const response = await apiClient.put('/system/feature-flags', {
      flags,
      change_reason: changeReason,
    });
    return response.data;
  },

  /**
   * Toggle a specific flag
   */
  toggleFlag: async (
    flagName: string,
    enabled: boolean,
    changeReason?: string
  ): Promise<{ flag_name: string; enabled: boolean; message: string }> => {
    const response = await apiClient.post(
      `/system/feature-flags/${flagName}/toggle`,
      null,
      { params: { enabled, change_reason: changeReason } }
    );
    return response.data;
  },

  /**
   * Get all user overrides
   */
  getUserOverrides: async (): Promise<UserOverridesResponse> => {
    const response = await apiClient.get('/system/feature-flags/user-overrides');
    return response.data;
  },

  /**
   * Get overrides for a specific user
   */
  getUserOverrideForUser: async (
    userId: string
  ): Promise<UserOverrideForUserResponse> => {
    const response = await apiClient.get(
      `/system/feature-flags/user-overrides/${encodeURIComponent(userId)}`
    );
    return response.data;
  },

  /**
   * Set user override
   */
  setUserOverride: async (data: UserOverrideRequest): Promise<void> => {
    await apiClient.post('/system/feature-flags/user-overrides', data);
  },

  /**
   * Delete user override for a specific flag
   */
  deleteUserOverride: async (
    userId: string,
    flagName: string,
    changeReason?: string
  ): Promise<void> => {
    await apiClient.delete(
      `/system/feature-flags/user-overrides/${encodeURIComponent(userId)}/${flagName}`,
      { params: { change_reason: changeReason } }
    );
  },

  /**
   * Delete all overrides for a user
   */
  deleteAllUserOverrides: async (
    userId: string,
    changeReason?: string
  ): Promise<void> => {
    await apiClient.delete(
      `/system/feature-flags/user-overrides/${encodeURIComponent(userId)}`,
      { params: { change_reason: changeReason } }
    );
  },

  /**
   * Get audit logs
   */
  getAuditLogs: async (
    limit = 50,
    offset = 0
  ): Promise<AuditLogsResponse> => {
    const response = await apiClient.get('/system/audit/feature-flags', {
      params: { limit, offset },
    });
    return response.data;
  },

  // ==================== User Group Management ====================

  /**
   * Get all user groups and their members
   */
  getGroups: async (): Promise<{ groups: GroupMembers }> => {
    const response = await apiClient.get('/system/feature-flags/groups');
    return response.data;
  },

  /**
   * Get members of a specific group
   */
  getGroupMembers: async (
    groupName: string
  ): Promise<{ group_name: string; members: string[] }> => {
    const response = await apiClient.get(
      `/system/feature-flags/groups/${encodeURIComponent(groupName)}`
    );
    return response.data;
  },

  /**
   * Add a user to a group
   */
  addUserToGroup: async (
    groupName: string,
    userId: string,
    changeReason?: string
  ): Promise<void> => {
    await apiClient.post('/system/feature-flags/groups/members', {
      group_name: groupName,
      user_id: userId,
      change_reason: changeReason,
    });
  },

  /**
   * Remove a user from a group
   */
  removeUserFromGroup: async (
    groupName: string,
    userId: string,
    changeReason?: string
  ): Promise<void> => {
    await apiClient.delete(
      `/system/feature-flags/groups/${encodeURIComponent(groupName)}/members/${encodeURIComponent(userId)}`,
      { params: { change_reason: changeReason } }
    );
  },

  /**
   * Delete an entire group
   */
  deleteGroup: async (
    groupName: string,
    changeReason?: string
  ): Promise<void> => {
    await apiClient.delete(
      `/system/feature-flags/groups/${encodeURIComponent(groupName)}`,
      { params: { change_reason: changeReason } }
    );
  },
};
